import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, retry } from 'rxjs/operators';
import { AppConfigService } from '../app-config.service';
import { Constants } from '../../models/constants.model';
import { AuthorizationService } from '../authorization.service';
import { MessageService } from '../message.service';
import { MessageType } from '../../models/message';
import { CacheService } from '../cache.service';
import { ArrayHelper } from '../../utils/array-helper';
import { StringHelper } from '../../utils/string-helper';
import { HttpHelper } from '../../utils/http-helper';
import { UrlHelper } from '../../utils/url-helper';
import { AvailableRoom } from '../../models/available-room';
import { AvailableDevice } from '../../models/available-device';
import { StatusId } from '../../models/status-id';

@Injectable({
  providedIn: 'root'
})
export abstract class BaseService<TEntity> {

  /**
   * Request-ContentType
   *
   * @protected
   * @memberof BaseService
   */
  protected contentType = Constants.ContentType.JSON;

  /**
   * Service required authentication
   *
   * @protected
   * @memberof BaseService
   */
  protected get authorizationType() {
    return AuthorizationType.Login;
  }

  /**
   * Base-url of rest-service
   *
   * @private
   * @type {string}
   * @memberof BaseService
   */
  protected apiBaseUrl: string;

  /**
   * Indicates when a login-redirect is pending
   *
   * @protected
   * @memberof BaseService
   */
  protected isRedirectPending = false;

  /**
   * Http-request options
   *
   * @private
   * @type {*}
   * @memberof BaseService
   */
  private httpOptions: any;

  /**
   * Number of retries before failing with a request
   *
   * @private
   * @type {number}
   * @memberof BaseService
   */
  private numRetry: number;

  constructor(
    private http: HttpClient,
    private authorization: AuthorizationService,
    private messages: MessageService,
    protected cache: CacheService
  ) {
    this.apiBaseUrl = UrlHelper.trimUrl(AppConfigService.settings.apiUrl);
    this.numRetry = AppConfigService.settings.debug ? 0 : 2;
    this.loadHttpOptions();
  }

  /**
   * Uri of the rest-endpoint
   *
   * @protected
   * @abstract
   * @type {string}
   * @memberof BaseService
   */
  protected abstract readonly endpointUri: string;

  abstract getAll(): Observable<TEntity[]>;

  abstract get(id: string): Observable<any>;

  abstract add(entity: TEntity, headers: IKeyValuePair<string, string>[]): Observable<any>;

  abstract update(entity: TEntity): Observable<any>;

  abstract delete(id: string): Observable<{}>;

  protected getAllRequest(url: string = null, params: IKeyValuePair<string, string>[] = null) {
    if (this.shouldReturnEmpty) {
      return this.emptyResponse;
    }

    const endpoint = url == null ? this.endpointUri : url;
    return this.http.get<TEntity[]>(this.getFullUrl(endpoint, null, params), this.httpOptions)
      .pipe(
        retry(this.numRetry),
        catchError(this.handleError<TEntity[]>(`GET: ${endpoint}`))
      );
  }

  protected getRequest<TResult>(id: string, url: string = null, params: IKeyValuePair<string, string>[] = null): any {
    if (this.shouldReturnEmpty) {
      return this.emptyResponse;
    }

    const endpoint = url == null ? this.endpointUri : url;
    return this.http.get<TResult>(this.getFullUrl(endpoint, id, params), this.httpOptions)
      .pipe(
        retry(this.numRetry),
        catchError(this.handleError<TEntity>(`GET: ${endpoint}`))
      );
  }

  protected getRequestCustom<TResult>(url: string = null): any {
    if (this.shouldReturnEmpty) {
      return this.emptyResponse;
    }

    return this.http.get<TResult>(url, this.httpOptions)
      .pipe(
        retry(this.numRetry),
        catchError(this.handleError<TEntity>(`GET-Custom: ${url}`))
      );
  }

  protected addRequest(url: string = null, entity: TEntity, headers: IKeyValuePair<string, string>[]): Observable<any> {
    if (this.shouldReturnEmpty) {
      return this.emptyResponse;
    }

    const endpoint = url == null ? this.endpointUri : url;
    const options = headers ? this.mergeHeaders(this.httpOptions, headers) : this.httpOptions;
    options.observe = 'response';
    return this.http.post<HttpResponse<TEntity>>(this.getFullUrl(endpoint, null), entity, options)
      .pipe(
        retry(this.numRetry),
        catchError(this.handleError<TEntity>(`POST: ${endpoint}`))
      );
  }

  protected updateRequest(id: string, url: string = null, entity: TEntity, headers: IKeyValuePair<string, string>[]): Observable<any> {
    if (this.shouldReturnEmpty) {
      return this.emptyResponse;
    }

    const endpoint = url == null ? this.endpointUri : url;
    const options = headers ? this.mergeHeaders(this.httpOptions, headers) : this.httpOptions;
    options.observe = 'response';
    return this.http.put<HttpResponse<TEntity>>(this.getFullUrl(endpoint, id), entity, this.httpOptions)
      .pipe(
        retry(this.numRetry),
        catchError(this.handleError<TEntity>(`PUT: ${endpoint}`))
      );
  }

  protected patchRequest(id: string, url: string = null, entity: TEntity, headers: IKeyValuePair<string, string>[]): Observable<any> {
    if (this.shouldReturnEmpty) {
      return this.emptyResponse;
    }

    const endpoint = url == null ? this.endpointUri : url;
    const options = headers ? this.mergeHeaders(this.httpOptions, headers) : this.httpOptions;
    options.observe = 'response';
    return this.http.patch<HttpResponse<TEntity>>(this.getFullUrl(endpoint, id), entity, this.httpOptions)
      .pipe(
        retry(this.numRetry),
        catchError(this.handleError<TEntity>(`PATCH: ${endpoint}`))
      );
  }

  protected deleteRequest(id: string, url: string = null): Observable<{}> {
    if (this.shouldReturnEmpty) {
      return this.emptyResponse;
    }

    const endpoint = url == null ? this.endpointUri : url;
    return this.http.delete(this.getFullUrl(endpoint, id), this.httpOptions)
      .pipe(
        catchError(this.handleError<TEntity>(`DELETE: ${endpoint}`))
      );
  }

  /**
   * Returns true if any operation is pending, which should prevent http-respones-handling
   *
   * @readonly
   * @private
   * @type {boolean}
   * @memberof BaseService
   */
  private get shouldReturnEmpty(): boolean {
    this.ensureLogin();
    return this.isRedirectPending;
  }

  /**
   * Returns an empty observeable
   *
   * @readonly
   * @private
   * @type {Observable<any>}
   * @memberof BaseService
   */
  private get emptyResponse(): Observable<any> {
    return new Observable();
  }

  /**
   * Handle request-errors
   *
   * @private
   * @template T
   * @param {string} [operation='unknown']
   * @param {T} [result]
   * @returns
   * @memberof BaseService
   */
  private handleError<T>(operation = 'unknown') {
    return (error: HttpErrorResponse): Observable<T> => {
      if (error.error instanceof ErrorEvent) {
        // A client-side or network error occurred.
        this.messages.add(operation + ': ' + error.error.message, MessageType.Error);
      } else {
        // The backend returned an unsuccessful response code.
        const err = 'Error on http-request for ' + error.url + ': ' + error.status + ' ' + HttpHelper.getStatusText(error.status);
        this.messages.add(`${operation} => ${err}`, MessageType.Debug);
      }

      return throwError(error);
    };
  }

  /**
   * Set endpoint headers
   *
   * @private
   * @returns {(void | never)}
   * @memberof BaseService
   */
  private loadHttpOptions(): void | never {
    const headers = {};
    headers[Constants.Header.Request.ContentType] = this.contentType;

    if (this.authorizationType !== AuthorizationType.None) {
      this.ensureLogin();
      // set header
      headers[Constants.Header.Request.Authorization] = this.authorization.getAuthorizationHeaderValue();
    }

    this.httpOptions = {
      headers: new HttpHeaders(headers)
    };
  }

  /**
   * Starts login if required
   *
   * @private
   * @return {*}  {(void | never)}
   * @memberof BaseService
   */
  private ensureLogin(): void | never {
    if (this.authorizationType === AuthorizationType.None || this.authorization.isAuthorized() || this.isRedirectPending) { return; }

    // redirect log oauth
    this.isRedirectPending = true;
    switch (this.authorizationType) {
      case AuthorizationType.Anonymous:
        this.authorization.logInAnonymous();
        break;
      case AuthorizationType.Login:
        this.authorization.logIn();
        break;
      default:
        this.messages.add('authorization for type "' + this.authorization + '" not implemented', MessageType.Debug);
    }
  }

  /**
   * Returns the full qualified url for an endpoint
   *
   * @private
   * @param {string} url
   * @param {IKeyValuePair[]} [params=null]
   * @returns {string}
   * @memberof BaseService
   */
  private getFullUrl(url: string, id: string, params: IKeyValuePair<string, string>[] = null): string {
    let result = this.apiBaseUrl + '/' + url + '/';
    // let result = this.apiBaseUrl + '/' + url + (url.includes('/') ? '' : '/');

    if (StringHelper.hasAny(id)) {
      result += id;
    }

    if (ArrayHelper.hasAny(params)) {
      result += '?' + params
        .filter(kv => !StringHelper.isNullOrEmpty(kv.value))
        .map(kv => `${kv.key}=${encodeURIComponent(kv.value)}`)
        .join('&');
    }

    return result;
  }

  private mergeHeaders(httpOptions: any, additionalHeaders: IKeyValuePair<string, string>[]): any {
    const keyValue = {};
    // existing headers
    httpOptions.headers.forEach((key: string, value: string[]) => { keyValue[key] = value[0]; });
    // additional header
    additionalHeaders.forEach(kv => { keyValue[kv.key] = kv.value; });
    // set headers
    httpOptions.headers = new HttpHeaders(keyValue);

    return httpOptions;
  }

  // TODO: reduce to minimal func calls, model return data
  public getReservabilityToken(room: AvailableRoom | AvailableDevice, searchDate: Date) {
      type AvailableResource = AvailableRoom | AvailableDevice;

    const pipe = [
      // room unavailable: 0 - 3
      (r: AvailableResource) => r.StatusId === (StatusId.rrGesperrtBis as number) && r.StatusDate >= searchDate,
      (r: AvailableResource) => r.StatusId === (StatusId.rrReservierbarBis as number) && r.StatusDate < searchDate,
      (r: AvailableResource) => r.StatusAllowOccupation !== true,
      (r: AvailableResource) => r.IsWorkingDay !== true,

      // rights available: 4 - 6
      (r: AvailableResource) => r.ReservationRightId === Constants.ReservationsRecht.Reservable,
      (r: AvailableResource) => r.ReservationRightId === Constants.ReservationsRecht.ProvReservable,
      (r: AvailableResource) => r.ReservationRightId === Constants.ReservationsRecht.Requestable,

      // rights unavailable: 7
      (r: AvailableResource) => r.ReservationRightId === Constants.ReservationsRecht.Visible,
    ];

    const appliedRule = pipe.findIndex(p => p(room));

    if (appliedRule === -1 || appliedRule > 7) {
      throw new Error('Mismatch: ' + room);
    }

    if (appliedRule < 4) {
      return { isRessourceAvailable: false, isBookingAllowed: false, actionLabel: null };
    }

    if (appliedRule < 7) {
      return { isRessourceAvailable: true, isBookingAllowed: true, actionLabel: appliedRule !== 6 ? 'RESERVE' : 'REQUEST' };
    }

    if (appliedRule === 7) {
      return { isRessourceAvailable: true, isBookingAllowed: false, actionLabel: null };
    }
  }

}

/**
 * Key/Value pair interface
 *
 * @interface IKeyValuePair
 */
export interface IKeyValuePair<TKey, TValue> {
  // todo tosc: move
  key: TKey;
  value: TValue;
}

export enum AuthorizationType {
  // No token required
  None = 1,
  // Anonymous token in scope Public
  Anonymous = 2,
  // Authenticate
  Login = 3,
}
